import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { createElement, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { SelectItem } from "@fronterahealth/frontera-ui-components";
import { Badge } from "@fronterahealth/frontera-ui-components/ui/badge";
import { Field, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";

import { BaseFieldType } from "@api/graphql/types-and-hooks";
import { ACTIVE_REVIEW_FIELD_CLASSNAME, useFieldReview } from "@components/QaReview/hooks/useFieldReview";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";
import { getTreatmentPlanFieldInput } from "@components/ReportField/TreatmentPlanField/registry";
import { SaveFieldsResult } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";

type SuggestionProps =
  | { suggestionFieldId: string; suggestionCount: number }
  | { suggestionFieldId?: never; suggestionCount?: never };

interface TreatmentPlanFieldBaseProps {
  fieldIdentifier: string;
  fieldType: BaseFieldType;
  value: string | null | undefined;
  onSave: (fieldIdentifier: string, value: string) => Promise<SaveFieldsResult>;
  title: string;
  required?: boolean;
  selectOptions?: SelectItem[];
  isReportCompleted?: boolean;
  isReadOnly?: boolean;
  variant?: "default" | "ghost";
}

type TreatmentPlanFieldProps = TreatmentPlanFieldBaseProps & SuggestionProps;

export const TreatmentPlanField: React.FC<TreatmentPlanFieldProps> = ({
  fieldIdentifier,
  fieldType,
  value,
  onSave,
  title,
  required = false,
  selectOptions = [],
  isReportCompleted = false,
  isReadOnly = false,
  variant = "ghost",
  suggestionFieldId,
  suggestionCount = 0,
}) => {
  const { isActive, activate, deactivate } = useFieldReview();
  const isReviewPanelVisible = useIsReviewPanelVisible();
  const [content, setContent] = useState(value ?? "");

  const [syncedValue, setSyncedValue] = useState(value);
  const isFocusedRef = useRef(false);

  if (value !== syncedValue && !isFocusedRef.current) {
    setSyncedValue(value);
    setContent(value ?? "");
  }

  const debounceSave = useDebouncedCallback((next: string) => {
    setSyncedValue(next);
    onSave(fieldIdentifier, next);
  }, 500);

  const fieldInput = getTreatmentPlanFieldInput(fieldType);
  if (!fieldInput) return null;

  const inputId = `${suggestionFieldId ?? fieldIdentifier}-input`;

  const onChange = async (next: string) => {
    setContent(next);
    debounceSave(next);
  };

  const onFocus = () => {
    isFocusedRef.current = true;
    activate(suggestionFieldId);
  };
  const onBlur = () => {
    isFocusedRef.current = false;
    debounceSave.flush();
    deactivate(suggestionFieldId);
  };

  const showBadge = suggestionCount > 0 && suggestionFieldId !== undefined && isReviewPanelVisible;

  const isFieldReviewed = isActive(suggestionFieldId) && showBadge;
  const finalVariant = isFieldReviewed ? "default" : variant;

  return (
    <Field
      id={suggestionFieldId ?? fieldIdentifier}
      className={clsx(isFieldReviewed && ACTIVE_REVIEW_FIELD_CLASSNAME)}
      data-active-review-field={isFieldReviewed || undefined}
      data-testid={`treatment-plan-field.${fieldIdentifier}`}
    >
      {title && (
        <FieldLabel htmlFor={inputId}>
          {title}
          {required && (
            <span aria-label="required" className="text-destructive">
              *
            </span>
          )}
          {showBadge && (
            <Badge
              variant="outline"
              size="sm"
              data-suggestion-indicator={suggestionFieldId}
              className={clsx(
                "ml-auto text-yellow-600",
                isActive(suggestionFieldId) ? "bg-yellow-100" : "bg-transparent",
              )}
            >
              <ExclamationTriangleIcon data-icon="inline-start" /> {suggestionCount}
            </Badge>
          )}
        </FieldLabel>
      )}
      {createElement(fieldInput, {
        id: inputId,
        value: content,
        onChange,
        selectOptions,
        variant: finalVariant,
        placeholder: "--",
        disabled: isReportCompleted || isReadOnly,
        onFocus,
        onBlur,
      })}
    </Field>
  );
};
